// The payment voucher document (P4.4; 08-DOCUMENTS "Payment voucher", D60): one bilingual A4 page,
// the first page of the Finance pack (P4.5). Same approach as the PO and DN documents: one
// renderer, values escaped, classes only. Bank details are masked (last 4 of the IBAN).

import { both, esc, field, or, PO_CSS, stripeHtml, T } from '../po/document';
import { entityLogo } from '../po/logo';
import { amountInWords } from '../rules/words';
import type { ChecklistItem, VoucherBody } from './voucher';

const money = (minor: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    minor / 100,
  );

const ITEM: Record<ChecklistItem, [string, string]> = {
  prApproved: ['PR approved', 'الطلب معتمد'],
  cfoSigned: ['PO signed by the CFO', 'أمر الشراء موقّع من المدير المالي'],
  supplierDocs: ['Supplier documents valid', 'مستندات المورد سارية'],
  bankLetter: ['Bank letter matches IBAN', 'خطاب البنك مطابق للآيبان'],
  delivery: ['Delivery note (or contract service)', 'سند الاستلام (أو خدمة تعاقدية)'],
  invoice: ['Invoice matches PO and DN', 'الفاتورة مطابقة لأمر الشراء والسند'],
};

const MILESTONE: Record<string, [string, string]> = {
  advance: ['Advance', 'دفعة مقدمة'],
  delivery: ['On delivery', 'عند التسليم'],
  net30: ['Net 30', 'خلال 30 يوماً'],
  milestone: ['Milestone', 'مرحلة'],
};

const tick = (ok: boolean) => (ok ? '✓' : '✕');

/** The voucher page (body only). */
export function renderPvDocument(d: VoucherBody): string {
  const cur = esc(d.currency);
  const words = amountInWords(d.payment.due, d.currency);
  const done = d.checklist.filter((c) => c.ok).length;
  const [mEn, mAr] = MILESTONE[d.payment.milestone] ?? ['Payment', 'دفعة'];
  const card = (n: number, en: string, ar: string, body: string, extra = '') =>
    `<div class="pod-box pvd-card ${extra}"><h2>${both(`${n}. ${en}`, ar)}</h2>${body}</div>`;
  const dns = d.deliveries.length
    ? d.deliveries
        .map(
          (x) =>
            `${esc(x.dnNo)} · ${esc(x.date)} · ${x.partial ? 'partial · جزئي' : 'complete · مكتمل'}`,
        )
        .join('<br>')
    : d.deliveryState === 'notRequired'
      ? 'Contract service, no DN · خدمة تعاقدية'
      : '—';

  return `<article class="pod pvd" lang="en">
<section class="pod-page">
${stripeHtml()}
<header class="pod-head">
<div class="pod-brand">${entityLogo(d.entity.code)}</div>
<div class="pod-title"><h1>PAYMENT VOUCHER</h1><p lang="ar" dir="rtl">سند صرف</p></div>
</header>
<div class="pod-refs pvd-refs">
${field('PV no.', 'رقم السند', `<strong>${esc(d.pvNo)}</strong>`)}
${field('Date', 'التاريخ', esc(d.date))}
${field('Entity · office', 'الجهة · المكتب', `${esc(d.entity.code)} · ${esc(d.office)}`)}
${field('Cost centre', 'مركز التكلفة', esc(d.vertical))}
${field('Currency', 'العملة', cur)}
</div>
<div class="pvd-grid">
${card(
  1,
  'Purchase request',
  'طلب الشراء',
  `${field('PR no.', 'رقم الطلب', esc(d.pr.prNo))}${field('Item', 'البند', esc(d.pr.title))}${field('Department', 'الإدارة', `${esc(d.pr.department)} · ${esc(d.vertical)}`)}${field('Raised', 'تاريخ الطلب', or(d.pr.raised, '—'))}`,
)}
${card(
  2,
  'Purchase order',
  'أمر الشراء',
  `${field('PO no.', 'رقم أمر الشراء', esc(d.po.poNo))}${field('Value', 'القيمة', `${cur} ${money(d.po.subtotal)} + VAT ${esc(d.po.vatRate)}% ${money(d.po.vat)} = ${money(d.po.total)}`)}${field('CFO approval', 'اعتماد المدير المالي', d.po.cfo.signed ? `Digitally approved · ${esc(d.po.cfo.at)}` : 'Pending · بانتظار التوقيع')}`,
)}
${card(
  3,
  'Supplier',
  'المورد',
  `${field('Legal name', 'الاسم القانوني', esc(d.supplier.name))}${field('CR · VAT', 'السجل · الضريبة', `${or(d.supplier.cr, '—')} · ${or(d.supplier.vat, '—')}`)}${field('Bank', 'البنك', or(d.supplier.bankName, '—'))}${field('IBAN', 'الآيبان', `<span dir="ltr">${or(d.supplier.iban, '—')}</span> ${tick(d.supplier.holderMatches)} holder matches legal name`)}${field('Bank letter', 'خطاب البنك', `${d.supplier.bankLetterOnFile ? 'On file · محفوظ' : 'Missing · غير موجود'}${d.supplier.bankChangedAt ? ` · last changed ${esc(d.supplier.bankChangedAt)}` : ''}`)}`,
)}
${card(4, 'Delivery', 'الاستلام', `${field('Delivery notes', 'سندات الاستلام', dns)}`)}
${card(
  5,
  'Supplier invoice',
  'فاتورة المورد',
  `${field('Invoice', 'الفاتورة', `${esc(d.invoice.invoiceNo)} · ${esc(d.invoice.date)}`)}${field('Amount', 'المبلغ', `${cur} ${money(d.invoice.total)}`)}${field('Match', 'المطابقة', d.invoice.match.matched ? '✓ matches PO and DN · مطابقة' : '✕ does not match · غير مطابقة')}`,
)}
${card(
  6,
  'Amount payable',
  'المبلغ المستحق',
  `<p class="pvd-amount"><span>${cur}</span> ${money(d.payment.due)}</p><p class="pod-words">${esc(words.en)}</p><p class="pod-words" lang="ar" dir="rtl">${esc(words.ar)}</p>${field('Payment', 'الدفعة', `${esc(mEn)}${d.payment.label ? ` (${esc(d.payment.label)})` : ''} · ${esc(d.payment.pct)}% · <span lang="ar">${esc(mAr)}</span>`)}${field('Paid before · balance after', 'المدفوع سابقاً · الرصيد بعد', `${money(d.payment.paidBefore)} · ${money(d.payment.balanceAfter)}`)}`,
  'pvd-card--pay',
)}
</div>
<div class="pvd-check">
<h2>${both(`Completeness check · ${done} of ${d.checklist.length}`, `التحقق من الاكتمال · ${done} من ${d.checklist.length}`)}</h2>
<ul>${d.checklist
    .map(
      (c) =>
        `<li class="${c.ok ? '' : 'pvd-miss'}">${tick(c.ok)} ${esc(ITEM[c.id][0])} · <span lang="ar">${esc(ITEM[c.id][1])}</span>${c.note === 'notNeeded' ? ' (not needed · غير مطلوب)' : ''}</li>`,
    )
    .join('')}</ul>
</div>
<footer class="pod-sign">
<div class="pod-sign__box pod-sign__box--ok"><span class="pod-label">PREPARED BY · PROCUREMENT · <span lang="ar">إعداد · المشتريات</span></span><strong>${esc(d.preparedByName || d.preparedBy)}</strong>${d.preparedByTitle ? `<span>${esc(d.preparedByTitle)}</span>` : ''}<span dir="ltr">${esc(d.preparedAt)} · digital</span></div>
<div class="pod-sign__box"><span class="pod-label">RECEIVED BY · FINANCE · <span lang="ar">استلام · المالية</span></span><strong>${or(d.receivedByName || d.receivedBy, '—')}</strong>${d.receivedByTitle ? `<span>${esc(d.receivedByTitle)}</span>` : ''}<span dir="ltr">${or(d.receivedAt, '')}</span></div>
<div class="pod-sign__box"><span class="pod-label">PAID · <span lang="ar">مدفوع</span></span><strong>${d.paymentRef ? `Ref ${esc(d.paymentRef)}` : '—'}</strong><span dir="ltr">${d.paidOn ? `${esc(d.paidOn)} · ${cur} ${money(d.payment.due)}` : ''}</span></div>
</footer>
<p class="pod-foot"><span>Attachments: PO, DN, invoice, CR, VAT certificate, bank letter · Bank details shown to Procurement and Finance only · <span lang="ar">بيانات البنك للمشتريات والمالية فقط</span></span><span>Page 1 of 1 · <span dir="ltr">${esc(d.pvNo)}</span></span></p>
</section>
</article>`;
}

/** PV additions to the PO document styles. */
export const PV_CSS = `${PO_CSS}
.pvd-refs{grid-template-columns:repeat(5,1fr)}
.pvd-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.pvd-card .pod-field{flex-direction:row;gap:8px}
.pvd-card .pod-label{min-width:110px}
.pvd-card .pod-value{font-family:inherit}
.pvd-card--pay{grid-column:1 / -1;background:${T('accent-soft')};border-color:${T('accent')}}
.pvd-amount{margin:0;font-family:"IBM Plex Mono",Consolas,monospace;font-size:18px;font-weight:700}
.pvd-check{border:1px solid ${T('border-strong')};border-radius:6px;padding:6px 8px}
.pvd-check h2{display:flex;justify-content:space-between;margin:0 0 4px;font-size:10px;color:${T('accent')}}
.pvd-check ul{display:grid;grid-template-columns:1fr 1fr;gap:2px 12px;margin:0;padding:0;list-style:none}
.pvd-miss{color:${T('danger-ink')};font-weight:700}
`;

export function pvHtmlFile(d: VoucherBody): string {
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${esc(d.pvNo)}</title><style>${PV_CSS}</style></head><body>${renderPvDocument(d)}</body></html>`;
}
